// Widget bodies for the desktop surface (CR #133).
//
// Every widget here is an EXISTING dashboard component fed by the existing
// dashboard API — the desktop is a new arrangement of the same information, not
// a second source of truth. Data is fetched once per surface and only for the
// widget kinds actually on screen.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getDashboardStats, getUpcomingJobs, getAlerts, getCalendarEvents,
  getNeedsAttention, getDailyLogStatus, getJobTaskLists, getRevenueTrend,
  getRecentActivity,
} from '@/lib/api/dashboard';
import type {
  DashboardStats, UpcomingJob, Alert, CalendarEvent, DailyLogJobStatus,
  JobTaskCard, RevenueTrendPoint, ActivityItem,
} from '@/lib/api/dashboard';
import { WeatherStrip, type DayForecast } from '@/components/dashboard/WeatherStrip';
import { MiniCalendar } from '@/components/dashboard/MiniCalendar';
import { AlertsList } from '@/components/dashboard/AlertsList';
import { NeedsAttentionPanel } from '@/components/dashboard/NeedsAttentionPanel';
import { UpcomingJobs } from '@/components/dashboard/UpcomingJobs';
import { QuickDailyLog } from '@/components/dashboard/QuickDailyLog';
import { JobTaskLists } from '@/components/dashboard/JobTaskLists';
import { RevenueTrendChart } from '@/components/dashboard/RevenueTrendChart';
import { JobsPipeline } from '@/components/dashboard/JobsPipeline';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { useWindowManager } from '@/lib/context/WindowManagerContext';
import type { WidgetKind } from '@/lib/desktop/desktopLayout';

export interface DesktopData {
  stats: DashboardStats | null;
  upcoming: UpcomingJob[];
  alerts: Alert[];
  events: CalendarEvent[];
  attention: Alert[];
  dailyLogJobs: DailyLogJobStatus[];
  jobTasks: JobTaskCard[];
  revenue: RevenueTrendPoint[];
  activity: ActivityItem[];
  forecast: DayForecast[];
  setForecast: (d: DayForecast[]) => void;
  reload: () => void;
}

/** `kindsKey` is a sorted, comma-joined list of visible widget kinds — a stable
 *  primitive so adding/removing widgets refetches but re-renders don't. */
export function useDesktopData(kindsKey: string): DesktopData {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [upcoming, setUpcoming] = useState<UpcomingJob[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [attention, setAttention] = useState<Alert[]>([]);
  const [dailyLogJobs, setDailyLogJobs] = useState<DailyLogJobStatus[]>([]);
  const [jobTasks, setJobTasks] = useState<JobTaskCard[]>([]);
  const [revenue, setRevenue] = useState<RevenueTrendPoint[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [forecast, setForecast] = useState<DayForecast[]>([]);

  const reload = useCallback(() => {
    const kinds = new Set(kindsKey.split(','));
    if (kinds.has('stats') || kinds.has('pipeline')) getDashboardStats().then(setStats).catch(() => {});
    if (kinds.has('upcoming')) getUpcomingJobs().then(setUpcoming).catch(() => {});
    if (kinds.has('alerts')) getAlerts().then(setAlerts).catch(() => {});
    if (kinds.has('calendar')) getCalendarEvents().then(setEvents).catch(() => {});
    if (kinds.has('attention')) getNeedsAttention().then(setAttention).catch(() => {});
    if (kinds.has('dailylog')) getDailyLogStatus().then(setDailyLogJobs).catch(() => {});
    if (kinds.has('tasklists')) getJobTaskLists().then(setJobTasks).catch(() => {});
    if (kinds.has('revenue')) getRevenueTrend().then(setRevenue).catch(() => {});
    if (kinds.has('activity')) getRecentActivity().then(setActivity).catch(() => {});
  }, [kindsKey]);

  useEffect(() => { reload(); }, [reload]);

  // Stable identity so memoised widget frames only re-render when data changes,
  // not on every pointermove during a drag.
  return useMemo(
    () => ({ stats, upcoming, alerts, events, attention, dailyLogJobs, jobTasks, revenue, activity, forecast, setForecast, reload }),
    [stats, upcoming, alerts, events, attention, dailyLogJobs, jobTasks, revenue, activity, forecast, reload]
  );
}

const money = (n: number) =>
  `$${(n || 0).toLocaleString('en-CA', { maximumFractionDigits: 0 })}`;

function ClockWidget() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <p className="font-mono text-4xl font-bold tracking-tight text-gray-900">
        {now.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false })}
      </p>
      <p className="mt-1 text-sm font-semibold text-gray-600">
        {now.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' })}
      </p>
    </div>
  );
}

function StatTile({ label, value, to }: { label: string; value: string; to: string }) {
  return (
    <Link
      to={to}
      className="flex flex-col justify-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 hover:border-primary/50 hover:bg-white"
    >
      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</span>
      <span className="text-lg font-bold text-gray-900">{value}</span>
    </Link>
  );
}

function StatsWidget({ stats }: { stats: DashboardStats | null }) {
  if (!stats) return <p className="p-2 text-sm text-gray-400">Loading…</p>;
  return (
    <div className="grid grid-cols-2 gap-2">
      <StatTile label="Active Jobs" value={String(stats.activeJobs)} to="/jobs" />
      <StatTile label="Pipeline" value={money(stats.pipelineValue)} to="/quotes" />
      <StatTile label="A/R Outstanding" value={money(stats.arOutstanding)} to="/invoices" />
      <StatTile label="Overdue" value={`${stats.overdueCount} · ${money(stats.overdueAmount)}`} to="/invoices" />
      <StatTile label="Clients" value={String(stats.totalClients)} to="/clients" />
      <StatTile label="Crew" value={String(stats.activeEmployees)} to="/employees" />
    </div>
  );
}

const Empty = ({ text }: { text: string }) => (
  <p className="flex h-full items-center justify-center text-sm text-gray-400">{text}</p>
);

// CR #184 — the dashboard header's actions as a widget; everything opens as a
// floating window (#182).
function QuickActionsWidget() {
  const { openWindow } = useWindowManager();
  const actions = [
    { route: '/jobs/new', label: 'New Job', icon: 'add' },
    { route: '/quotes/new', label: 'New Quote', icon: 'request_quote' },
    { route: '/invoices', label: 'Invoices', icon: 'receipt_long' },
    { route: '/my-tasks', label: 'My Tasks', icon: 'checklist' },
  ];
  return (
    <div className="grid h-full grid-cols-2 gap-2">
      {actions.map(a => (
        <button
          key={a.route}
          onClick={() => openWindow({ route: a.route, title: a.label, icon: a.icon })}
          className="flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2 text-sm font-semibold text-gray-800 hover:border-primary/50 hover:bg-white"
        >
          <span className="material-symbols-outlined text-base text-primary">{a.icon}</span>
          {a.label}
        </button>
      ))}
    </div>
  );
}

export const DesktopWidgetBody = ({ kind, data }: { kind: WidgetKind; data: DesktopData }) => {
  switch (kind) {
    case 'clock':
      return <ClockWidget />;
    case 'stats':
      return <StatsWidget stats={data.stats} />;
    case 'weather':
      // Renders nothing when the weather API is unreachable — keep a hint so the
      // frame doesn't look broken/empty.
      return (
        <>
          <WeatherStrip onForecast={data.setForecast} />
          {data.forecast.length === 0 && <Empty text="Forecast unavailable" />}
        </>
      );
    case 'calendar':
      return <MiniCalendar events={data.events} forecast={data.forecast} />;
    case 'alerts':
      return data.alerts.length ? <AlertsList alerts={data.alerts} /> : <Empty text="Nothing needs approval" />;
    case 'attention':
      return data.attention.length
        ? <NeedsAttentionPanel items={data.attention} />
        : <Empty text="No data gaps — all clean" />;
    case 'upcoming':
      return <UpcomingJobs jobs={data.upcoming} />;
    case 'dailylog':
      return <QuickDailyLog jobs={data.dailyLogJobs} onLogged={data.reload} />;
    case 'tasklists':
      return <JobTaskLists tasks={data.jobTasks} onChanged={data.reload} />;
    case 'revenue':
      return <RevenueTrendChart data={data.revenue} />;
    case 'pipeline':
      return data.stats ? <JobsPipeline stats={data.stats} /> : <Empty text="Loading…" />;
    case 'activity':
      return data.activity.length ? <RecentActivity items={data.activity} /> : <Empty text="No recent activity" />;
    case 'quickactions':
      return <QuickActionsWidget />;
    default:
      return null;
  }
};
